import React, { useState } from 'react';

/**
 * CrisisScreen
 * Full-screen compassionate intervention for users showing signs of acute distress.
 * Follows WHO Safe Messaging Guidelines — no graphic language, no alarm, warm and human.
 * Props:
 *   protocol  — { has_history, suggestions, custom_meditations } from /api/users/{id}/crisis-protocol
 *   onContinue / onDismiss — callbacks
 */

const CRISIS_RESOURCES = [
  { country: 'Portugal', name: 'SOS Voz Amiga', phone: '213 544 545', available: '16h–24h', flag: '🇵🇹' },
  { country: 'Portugal', name: 'Linha de Apoio à Crise', phone: '800 209 200', available: '24/7', flag: '🇵🇹' },
  { country: 'UK', name: 'Samaritans', phone: '116 123', available: '24/7', flag: '🇬🇧' },
  { country: 'Germany', name: 'Telefonseelsorge', phone: '0800 111 0 111', available: '24/7', flag: '🇩🇪' },
  { country: 'France', name: 'Numéro National Prévention Suicide', phone: '3114', available: '24/7', flag: '🇫🇷' },
  { country: 'Spain', name: 'Telèfon de l\'Esperança', phone: '93 414 48 48', available: '24/7', flag: '🇪🇸' },
  { country: 'Netherlands', name: '113 Zelfmoordpreventie', phone: '0800 0113', available: '24/7', flag: '🇳🇱' },
  { country: 'International', name: 'Befrienders Worldwide', phone: 'befrienders.org', available: '24/7', flag: '🌍' },
];

export default function CrisisScreen({ onContinue, onDismiss, protocol }) {
  const [showResources, setShowResources] = useState(false);
  const suggestions = protocol?.suggestions || [];
  const customMeditations = protocol?.custom_meditations || [];

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      zIndex: 9999,
      background: 'linear-gradient(160deg, #0D2B2A 0%, #1E4A47 60%, #1E6B68 100%)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '32px 24px',
      overflowY: 'auto',
    }}>
      <div style={{
        maxWidth: '480px',
        width: '100%',
        textAlign: 'center',
      }}>

        {/* Orb / breathing visual */}
        <div style={{
          width: '72px',
          height: '72px',
          borderRadius: '50%',
          background: 'radial-gradient(circle at 35% 35%, rgba(255,255,255,0.25), rgba(30,107,104,0.4))',
          border: '2px solid rgba(255,255,255,0.2)',
          margin: '0 auto 32px',
          animation: 'pulse 4s ease-in-out infinite',
        }} />

        <style>{`
          @keyframes pulse {
            0%, 100% { transform: scale(1); opacity: 0.8; }
            50% { transform: scale(1.08); opacity: 1; }
          }
        `}</style>

        {/* Main message */}
        <h2 style={{
          fontFamily: "'Playfair Display', serif",
          fontSize: '1.8rem',
          color: '#fff',
          marginBottom: '20px',
          lineHeight: 1.3,
          fontWeight: 400,
        }}>
          Before we continue —<br />you are not alone in this.
        </h2>

        <p style={{
          color: 'rgba(255,255,255,0.75)',
          fontSize: '1rem',
          lineHeight: 1.7,
          marginBottom: '12px',
        }}>
          What you're feeling right now matters. HYPSEYA is here to support your integration practice — but for moments like this, reaching a human being matters more.
        </p>

        <p style={{
          color: 'rgba(255,255,255,0.6)',
          fontSize: '0.9rem',
          lineHeight: 1.6,
          marginBottom: '36px',
          fontStyle: 'italic',
        }}>
          There are people available right now, trained to listen without judgement.
        </p>

        {/* Personalized protocol — what helped before */}
        {(suggestions.length > 0 || customMeditations.length > 0) && (
          <div style={{ marginBottom: '28px', textAlign: 'left' }}>
            <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.72rem', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '12px' }}>
              What has helped you before
            </p>
            {suggestions.map((s, i) => (
              <div key={i} style={{
                background: 'rgba(255,255,255,0.07)',
                border: '1px solid rgba(255,255,255,0.12)',
                borderRadius: '12px', padding: '14px 16px',
                marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '14px',
              }}>
                <div style={{
                  width: '36px', height: '36px', borderRadius: '50%', flexShrink: 0,
                  background: 'rgba(45,139,135,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <circle cx="8" cy="8" r="6" stroke="rgba(255,255,255,0.7)" strokeWidth="1.2"/>
                    <path d="M8 5v3l2 2" stroke="rgba(255,255,255,0.7)" strokeWidth="1.2" strokeLinecap="round"/>
                  </svg>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ color: '#fff', fontWeight: '600', fontSize: '0.88rem' }}>{s.label}</div>
                  <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.75rem', marginTop: '2px' }}>
                    Brought you from {s.from_activation}/10 → {s.to_activation}/10 activation
                  </div>
                </div>
                {onContinue && (
                  <button onClick={onContinue} style={{
                    background: 'rgba(45,139,135,0.4)', border: 'none',
                    borderRadius: '8px', padding: '7px 12px',
                    color: '#fff', fontSize: '0.75rem', cursor: 'pointer', whiteSpace: 'nowrap',
                  }}>
                    Start →
                  </button>
                )}
              </div>
            ))}
            {customMeditations.slice(0, 1).map((m, i) => (
              <div key={i} style={{
                background: 'rgba(255,255,255,0.07)',
                border: '1px solid rgba(255,255,255,0.12)',
                borderRadius: '12px', padding: '14px 16px',
                marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '14px',
              }}>
                <div style={{
                  width: '36px', height: '36px', borderRadius: '50%', flexShrink: 0,
                  background: 'rgba(184,144,106,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path d="M7 1 C4.8 1 3 2.8 3 5 C3 7.5 5.5 10 7 13 C8.5 10 11 7.5 11 5 C11 2.8 9.2 1 7 1Z" fill="rgba(255,255,255,0.6)"/>
                  </svg>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ color: '#fff', fontWeight: '600', fontSize: '0.88rem' }}>Your meditation: {m.title}</div>
                  <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.75rem', marginTop: '2px' }}>
                    {m.duration_minutes} min · your voice
                  </div>
                </div>
                {onContinue && (
                  <button onClick={onContinue} style={{
                    background: 'rgba(45,139,135,0.4)', border: 'none',
                    borderRadius: '8px', padding: '7px 12px',
                    color: '#fff', fontSize: '0.75rem', cursor: 'pointer', whiteSpace: 'nowrap',
                  }}>
                    Start →
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Show resources toggle */}
        {!showResources ? (
          <button
            onClick={() => setShowResources(true)}
            style={{
              display: 'block',
              width: '100%',
              padding: '16px',
              background: 'rgba(255,255,255,0.15)',
              border: '1.5px solid rgba(255,255,255,0.3)',
              borderRadius: '14px',
              color: '#fff',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: 'pointer',
              marginBottom: '16px',
              letterSpacing: '0.02em',
            }}
          >
            Show support lines near me
          </button>
        ) : (
          <div style={{ marginBottom: '24px' }}>
            {CRISIS_RESOURCES.map((r, i) => (
              <div key={i} style={{
                background: 'rgba(255,255,255,0.08)',
                border: '1px solid rgba(255,255,255,0.15)',
                borderRadius: '12px',
                padding: '14px 16px',
                marginBottom: '10px',
                textAlign: 'left',
                display: 'flex',
                alignItems: 'center',
                gap: '14px',
              }}>
                <span style={{ fontSize: '1.4rem' }}>{r.flag}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ color: '#fff', fontWeight: '600', fontSize: '0.9rem' }}>{r.name}</div>
                  <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.8rem' }}>{r.country} · {r.available}</div>
                </div>
                <div style={{
                  color: '#7DD8D4',
                  fontWeight: '700',
                  fontSize: '0.95rem',
                  whiteSpace: 'nowrap',
                }}>
                  {r.phone}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Disclaimer */}
        <p style={{
          color: 'rgba(255,255,255,0.4)',
          fontSize: '0.75rem',
          lineHeight: 1.5,
          marginBottom: '32px',
        }}>
          HYPSEYA is a wellness and emotional regulation tool — not a substitute for professional mental health support or emergency services. If you are in immediate danger, please call your local emergency number (112 in Europe).
        </p>

        {/* Actions */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {onContinue && (
            <button
              onClick={onContinue}
              style={{
                padding: '14px',
                background: 'transparent',
                border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: '12px',
                color: 'rgba(255,255,255,0.5)',
                fontSize: '0.85rem',
                cursor: 'pointer',
              }}
            >
              I'm okay — continue to meditation
            </button>
          )}
          {onDismiss && (
            <button
              onClick={onDismiss}
              style={{
                padding: '14px',
                background: 'rgba(255,255,255,0.1)',
                border: 'none',
                borderRadius: '12px',
                color: 'rgba(255,255,255,0.7)',
                fontSize: '0.85rem',
                cursor: 'pointer',
              }}
            >
              Go back
            </button>
          )}
        </div>

      </div>
    </div>
  );
}
